import TextOption from "@/components/TextOption";
import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/api";
import StatusBadge from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { EmptyState, ErrorState, ListSkeleton, Refreshing } from "@/components/admin/AsyncView";
import { fmtShort } from "@/components/admin/format";

const PAGE_SIZE = 25;
const STATUSES = ["", "PENDING", "APPROVED", "BORROWED", "RETURN_REQUESTED", "RETURNED", "COMPLETED", "IN_PROGRESS", "OVERDUE", "REJECTED", "CANCELLED"];
const STATUS_LABEL = { "": "Semua status", COMPLETED: "Acara selesai", IN_PROGRESS: "Acara berlangsung", PENDING: "Menunggu", APPROVED: "Disetujui", BORROWED: "Dipinjam", RETURN_REQUESTED: "Pengembalian", RETURNED: "Selesai", OVERDUE: "Terlambat", REJECTED: "Ditolak", CANCELLED: "Dibatalkan" };

// Riwayat/pencarian booking dengan dukungan server (q/status/limit/offset).
// Tidak pernah disebut "semua riwayat" bila backend membatasi.
export default function BookingsPage() {
  const [params, setParams] = useSearchParams();
  const q = params.get("q") || "";
  const status = params.get("status") || "";
  const bookingType = params.get("booking_type") || "";
  const locationId = params.get("location_id") || "";
  const accessState = params.get("access_state") || "";
  const [locations, setLocations] = useState([]);
  useEffect(() => { api.get("/admin/locations").then(({data}) => setLocations(data)).catch(() => {}); }, []);
  const page = Math.max(1, Number(params.get("page")) || 1);

  const [rows, setRows] = useState(null);
  const [total, setTotal] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);

  const set = (key, value, replace = true) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value); else next.delete(key);
    if (key !== "page") next.delete("page");
    setParams(next, { replace });
  };

  useEffect(() => {
    let alive = true;
    const background = rows !== null;
    if (background) setRefreshing(true); else { setLoading(true); setError(null); }
    Promise.all([
      api.get("/admin/bookings", { params: { q: q || undefined, status: status || undefined, booking_type: bookingType || undefined, location_id: locationId || undefined, access_state: accessState || undefined, limit: PAGE_SIZE + 1, offset: (page - 1) * PAGE_SIZE } }),
      (!q && !status && !bookingType && !locationId && !accessState) ? api.get("/admin/stats").catch(() => null) : Promise.resolve(null),
    ]).then(([bookingsRes, statsRes]) => {
      if (!alive) return;
      const data = bookingsRes.data;
      setHasMore(data.length > PAGE_SIZE);
      setRows(data.slice(0, PAGE_SIZE));
      setTotal(statsRes?.data?.total_bookings ?? null);
      setError(null);
    }).catch((e) => {
      if (!alive) return;
      if (!background) setError(errMsg(e));
      else toast.error(`Tidak dapat memperbarui: ${errMsg(e)}. Menampilkan data terakhir.`);
    }).finally(() => {
      if (!alive) return;
      setLoading(false); setRefreshing(false);
    });
    return () => { alive = false; };
  }, [q, status, bookingType, locationId, accessState, page, reloadKey]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) {
    return (
      <div className="space-y-5">
        <BookingsHeader total={null} />
        <ListSkeleton rows={6} />
      </div>
    );
  }
  if (error && rows === null) {
    return (
      <div className="space-y-5">
        <BookingsHeader total={null} />
        <ErrorState message={error} onRetry={() => { setRows(null); setLoading(true); setError(null); setReloadKey((k) => k + 1); }} />
      </div>
    );
  }

  const filtered = q || status || bookingType || locationId || accessState;
  const start = (page - 1) * PAGE_SIZE;
  const backTo = `/admin/peminjaman?${params.toString()}`;

  return (
    <div className="space-y-5">
      <BookingsHeader total={total} refreshing={refreshing} filtered={filtered} />

      <div className="flex flex-wrap items-center gap-2">
        <label className="flex h-10 min-w-0 basis-full items-center gap-2 rounded-lg border border-white/10 bg-zinc-900 px-3 sm:basis-auto sm:flex-1 sm:max-w-xs">
          <span className="sr-only">Cari kode, nama, atau keperluan</span>
          <Input data-testid="bookings-search" value={q} onChange={(e) => set("q", e.target.value || null)}
            placeholder="Cari kode, nama, keperluan" className="h-full border-0 bg-transparent p-0 text-sm placeholder:text-zinc-600 focus-visible:ring-0" />
        </label>
        <select data-testid="bookings-status-filter" value={status} onChange={(e) => set("status", e.target.value || null)}
          aria-label="Filter status" className="h-10 rounded-lg border border-white/10 bg-zinc-900 px-2 text-sm">
          {STATUSES.map((s) => <TextOption key={s} value={s} label={STATUS_LABEL[s]} />)}
        </select>
        <select aria-label="Filter jenis booking" value={bookingType} onChange={e=>set("booking_type",e.target.value)} className="h-10 rounded-lg border border-white/10 bg-zinc-900 px-2 text-sm"><option value="">Semua jenis</option><option value="WITH_EQUIPMENT">Dengan barang</option><option value="LOCATION_ONLY">Tanpa barang</option></select>
        <select aria-label="Filter lokasi" value={locationId} onChange={e=>set("location_id",e.target.value)} className="h-10 max-w-[220px] rounded-lg border border-white/10 bg-zinc-900 px-2 text-sm"><option value="">Semua lokasi</option>{locations.map(l=><TextOption key={l.id} value={l.id} label={l.name} />)}</select>
        <select aria-label="Filter pemeriksaan akses" value={accessState} onChange={e => set("access_state", e.target.value)} className="h-10 rounded-lg border border-white/10 bg-zinc-900 px-2 text-sm"><option value="">Semua akses</option><option value="NEEDS_REVIEW">Akses perlu diperiksa</option><option value="REVIEW_CLOSED">Akses tetap diblokir</option></select>
        <span data-testid="bookings-count" className="ml-auto text-xs tabular-nums text-zinc-500">
          {rows.length === 0 ? "Tidak ada hasil" : `Menampilkan ${start + 1}-${start + rows.length}${!filtered && total != null ? ` dari ${total}` : ""} booking`}
        </span>
      </div>

      {rows.length === 0 ? (
        <EmptyState
          kind={filtered ? "filtered" : "first"}
          title={filtered ? "Tidak ada hasil" : "Belum ada booking"}
          hint={filtered ? "Coba ubah kata kunci atau status." : "Booking yang masuk akan tampil di sini."}
          action={filtered && (
            <Button variant="outline" onClick={() => setParams({}, { replace: true })}
              className="h-10 rounded-full border-white/15 bg-transparent px-4 text-sm text-zinc-200 hover:bg-white/10">
              Bersihkan filter
            </Button>
          )}
        />
      ) : (
        <ol data-testid="all-bookings-list" className="divide-y divide-white/5 overflow-hidden rounded-2xl border border-white/10 bg-zinc-900">
          {rows.map((b) => (
            <li key={b.id}>
              <Link
                to={`/admin/peminjaman/${b.id}`}
                state={{ backTo, backLabel: "Kembali ke peminjaman" }}
                data-testid={`booking-row-${b.code}`}
                className="grid w-full grid-cols-[minmax(0,1fr)_auto] items-center gap-x-3 gap-y-1 px-4 py-3 text-left transition-colors min-h-[56px] hover:bg-white/5"
              >
                <span className="min-w-0">
                  <span className="block font-mono text-xs text-zinc-500">{b.code} &middot; {b.user_name || "-"}</span>
                  <span className="block truncate text-sm text-zinc-200">{b.purpose} · {(b.lines || []).map((l) => `${l.name} (${l.qty})`).join(", ")}</span>
                  <span className="block truncate text-xs text-zinc-500">{fmtShort(b.start_time)} - {fmtShort(b.end_time)} · {b.booking_type === "LOCATION_ONLY" ? "Tanpa barang" : "Dengan barang"} · {b.location_name_snapshot || b.custom_location || "Legacy"}</span>
                  {b.access_sync_state === "NEEDS_REVIEW" && <span className="block text-xs text-amber-400">Akses perlu diperiksa</span>}
                  {b.access_sync_state === "REVIEW_CLOSED" && <span className="block text-xs text-amber-400">Akses tetap diblokir</span>}
                </span>
                <StatusBadge status={b.status} />
              </Link>
            </li>
          ))}
        </ol>
      )}

      {(page > 1 || hasMore) && (
        <div className="flex items-center justify-between gap-2">
          <Button variant="outline" disabled={page <= 1 || loading} onClick={() => set("page", page > 2 ? String(page - 1) : null)}
            className="h-10 rounded-full border-white/15 bg-transparent px-4 text-sm text-zinc-200 hover:bg-white/10 disabled:opacity-40">
            Sebelumnya
          </Button>
          <span className="text-xs tabular-nums text-zinc-500">Halaman {page}</span>
          <Button variant="outline" disabled={!hasMore || loading} onClick={() => set("page", String(page + 1))}
            className="h-10 rounded-full border-white/15 bg-transparent px-4 text-sm text-zinc-200 hover:bg-white/10 disabled:opacity-40">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Berikutnya"}
          </Button>
        </div>
      )}
    </div>
  );
}

function BookingsHeader({ total, refreshing, filtered }) {
  return (
    <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
      <h1 className="text-2xl font-semibold tracking-tight text-white">Peminjaman</h1>
      <p className="text-sm text-zinc-400">
        {total == null ? (filtered ? "Hasil pencarian booking." : "Memuat riwayat…") : `${total} booking tercatat.`}
      </p>
      {refreshing && <Refreshing />}
    </div>
  );
}
